import type { ConsentAction, CookieCategory } from "./types";

interface ConsentReport {
	visitorId: string;
	choices: Record<string, boolean>;
	action: ConsentAction;
	bannerVersion: string | null;
}

interface QueuedReport {
	apiBase: string;
	apiKey: string;
	report: ConsentReport;
}

type DeliveryResult = "delivered" | "retry" | "discard";

const QUEUE_KEY = "cookieguard_report_queue";
const MAX_QUEUED_REPORTS = 10;

let visitorId: string | null = null;

function getVisitorId(): string {
	if (visitorId) return visitorId;

	const stored = localStorage.getItem("cookieguard_visitor_id");
	if (stored) {
		visitorId = stored;
		return stored;
	}

	visitorId = crypto.randomUUID();
	try {
		localStorage.setItem("cookieguard_visitor_id", visitorId);
	} catch {
		// localStorage unavailable
	}
	return visitorId;
}

export function reportConsent(
	apiBase: string,
	apiKey: string,
	choices: Record<CookieCategory, boolean>,
	action: ConsentAction,
	configVersion: number,
): void {
	const report: ConsentReport = {
		visitorId: getVisitorId(),
		choices,
		action,
		bannerVersion: String(configVersion),
	};
	const queuedReport = { apiBase, apiKey, report };

	void deliverReport(queuedReport).then((result) => {
		if (result === "retry") queueReports([queuedReport]);
	});
}

async function deliverReport(item: QueuedReport): Promise<DeliveryResult> {
	try {
		const response = await fetch(`${item.apiBase}/widget/consent/${item.apiKey}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(item.report),
			// Let the browser finish the request if the visitor navigates away.
			keepalive: true,
		});

		if (response.ok) return "delivered";
		if (response.status === 429 || response.status >= 500) return "retry";
		return "discard";
	} catch {
		return "retry";
	}
}

function queueReports(reports: QueuedReport[]): void {
	try {
		const raw = localStorage.getItem(QUEUE_KEY);
		const queue = raw ? (JSON.parse(raw) as QueuedReport[]) : [];
		const trimmed = [...queue, ...reports].slice(-MAX_QUEUED_REPORTS);
		localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
	} catch {
		// localStorage unavailable
	}
}

export async function flushReportQueue(): Promise<void> {
	let queue: QueuedReport[];

	try {
		const raw = localStorage.getItem(QUEUE_KEY);
		if (!raw) return;

		queue = JSON.parse(raw) as QueuedReport[];
		localStorage.removeItem(QUEUE_KEY);
	} catch {
		return;
	}

	for (const [index, item] of queue.entries()) {
		const result = await deliverReport(item);

		if (result === "retry") {
			// A throttled or unhealthy endpoint is unlikely to accept the rest of
			// the batch. Preserve it in order and try again on the next page load.
			queueReports(queue.slice(index));
			return;
		}
	}
}
